import React, { FC, useEffect, useRef, useState } from "react";
import { GoBell } from "react-icons/go";
import { LuCheck, LuSettings } from "react-icons/lu";
import { useLocation, useNavigate } from "react-router-dom";

import {
  useGetNotificationCountsQuery,
  useGetNotificationListQuery,
} from "../../slices/NotificationCenterSlice";
import { MarkAllDoneConfirm } from "./MarkAllDoneConfirm";
import { NotificationMiniList } from "./NotificationMiniList";
import { badgeText } from "./notifications.utils";
import { NotificationSettingsModal } from "./NotificationSettingsModal";
import { useNotificationActions } from "./useNotificationActions";

const SHOWN = 8;

/** The top bar's bell: unread count + drop-down with the newest notifications. */
export const NotificationBell: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId, open, setDone, doneAll, isDoingAll } =
    useNotificationActions();
  const [confirmAllDone, setConfirmAllDone] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const { data: counts } = useGetNotificationCountsQuery(
    { orgId },
    { skip: !orgId },
  );
  const { data: items, isLoading } = useGetNotificationListQuery(
    { orgId, view: "inbox", take: SHOWN },
    { skip: !orgId || !isOpen },
  );
  const isClearing = isDoingAll;
  const doneAvailable = counts?.doneAvailable !== false;

  // Close on outside click, Escape and page change.
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node))
        setIsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setIsOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen]);
  useEffect(() => setIsOpen(false), [location.pathname]);

  const badge = badgeText(counts?.unread);

  return (
    <div ref={wrapRef} className={"relative"}>
      <button
        type={"button"}
        aria-label={
          counts?.unread
            ? `Notifications, ${counts.unread} unread`
            : "Notifications"
        }
        aria-expanded={isOpen}
        onClick={() => setIsOpen((value) => !value)}
        className={`relative flex h-10 w-10 items-center justify-center rounded-full hover:bg-[#F1F2F5] ${
          isOpen ? "bg-[#F0EDFF] text-[#6B4EFF]" : "text-[#1F2433]"
        }`}>
        <GoBell className={"h-6 w-6"} />
        {badge && (
          <span
            className={
              "absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full border-2 border-white bg-[#E5484D] px-1 text-[10px] font-extrabold text-white"
            }>
            {badge}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          role={"dialog"}
          aria-label={"Notifications"}
          className={
            "absolute right-0 top-[48px] z-50 flex max-h-[min(560px,calc(100vh-90px))] w-[400px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-[14px] border border-[#E8E8EC] bg-white font-Manrope shadow-[0_18px_50px_rgba(31,36,51,0.18)]"
          }>
          <div
            className={
              "flex items-center justify-between gap-3 border-b border-[#F0F1F4] px-4 py-3"
            }>
            <div className={"flex items-center gap-2"}>
              <span className={"text-[15px] font-extrabold text-[#1F2433]"}>
                Notifications
              </span>
              {!!counts?.unread && (
                <span
                  className={
                    "rounded-full bg-[#F0EDFF] px-2 py-[1px] text-[11.5px] font-extrabold text-[#6B4EFF]"
                  }>
                  {counts.unread} unread
                </span>
              )}
            </div>
            <div className={"flex items-center gap-1"}>
              {doneAvailable && !!counts?.inbox && (
                <button
                  type={"button"}
                  disabled={isClearing}
                  onClick={() => {
                    // The drop-down closes; the confirm opens on its own.
                    setIsOpen(false);
                    setConfirmAllDone(true);
                  }}
                  className={
                    "flex h-8 items-center gap-1.5 rounded-[8px] px-2 text-[12.5px] font-bold text-[#6B4EFF] hover:bg-[#F0EDFF] disabled:opacity-60"
                  }>
                  <LuCheck /> Mark all as done
                </button>
              )}
              <button
                type={"button"}
                title={"Notification settings"}
                aria-label={"Notification settings"}
                onClick={() => setSettingsOpen(true)}
                className={
                  "flex h-8 w-8 items-center justify-center rounded-[8px] text-[16px] text-[#6B7185] hover:bg-[#F1F2F5] hover:text-[#1F2433]"
                }>
                <LuSettings />
              </button>
            </div>
          </div>

          <div className={"min-h-0 flex-1 overflow-y-auto px-4"}>
            {isLoading ? (
              <div className={"py-10 text-center text-[13px] text-[#9097A8]"}>
                Loading…
              </div>
            ) : !items?.length ? (
              <div className={"flex flex-col items-center gap-1.5 py-10"}>
                <span
                  className={
                    "mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-[#E9F7EF] text-[20px] text-[#2E9E62]"
                  }>
                  <LuCheck />
                </span>
                <span className={"text-[14px] font-bold text-[#1F2433]"}>
                  You&apos;re all caught up
                </span>
                <span className={"text-[12.5px] text-[#6B7185]"}>
                  New notifications will show up here.
                </span>
              </div>
            ) : (
              <NotificationMiniList
                items={items}
                onOpen={(item) => {
                  setIsOpen(false);
                  open(item);
                }}
                onDone={doneAvailable ? (item) => setDone([item]) : undefined}
              />
            )}
          </div>

          <button
            type={"button"}
            onClick={() => {
              setIsOpen(false);
              navigate("/notifications");
            }}
            className={
              "border-t border-[#F0F1F4] py-3 text-center text-[13px] font-extrabold text-[#6B4EFF] hover:bg-[#F7F5FF]"
            }>
            View all notifications
          </button>
        </div>
      )}

      {settingsOpen && (
        <NotificationSettingsModal onClose={() => setSettingsOpen(false)} />
      )}
      {confirmAllDone && (
        <MarkAllDoneConfirm
          count={counts?.inbox ?? null}
          unread={counts?.unread ?? null}
          onCancel={() => setConfirmAllDone(false)}
          onConfirm={() => {
            setConfirmAllDone(false);
            void doneAll("all");
          }}
        />
      )}
    </div>
  );
};
